/**
 * Extracts meaningful initials from a business name for use in monogram icons.
 *
 * Strips common business suffixes (Inc., LLC, Co., etc.), then returns
 * the first 1–2 initials from the remaining significant words.
 */

const SUFFIXES = [
    // Corporate forms — order matters (longer first to avoid partial matches)
    "incorporated",
    "corporation",
    "limited liability company",
    "limited liability co",
    "limited partnership",
    "limited",
    "professional corporation",
    "professional llc",
    "company",
    "enterprises",
    "enterprise",
    "holdings",
    "partners",
    "associates",
    "solutions",
    "services",
    "group",
    "intl",
    "international",
    "corp",
    "inc",
    "llc",
    "llp",
    "ltd",
    "lp",
    "co",
    "pc",
    "pllc",
    "dba",
  ],

  // Short filler words to skip when picking initials
  FILLER = new Set(["a", "an", "the", "and", "of", "for", "by", "in", "on", "at", "to", "&"]),

  /**
   * Remove trailing suffixes like ", Inc.", "LLC", "Co.", etc.
   * Handles with/without commas and periods.
   */
  stripSuffixes = (name: string): string => {
    let result = name,

      // Iteratively strip from the end — a name could have ", Inc." AND "LLC"
      changed = true;
    while (changed) {
      changed = false;
      for (const suffix of SUFFIXES) {
        // Match suffix at end, optionally preceded by comma/space, optionally followed by period
        const pattern = new RegExp(`[,\\s]+${suffix}\\.?\\s*$|\\b${suffix}\\.?\\s*$`, "i"),
          stripped = result.replace(pattern, "").trim();
        if (stripped !== result && stripped.length > 0) {
          result = stripped;
          changed = true;
          break; // Restart loop with shorter string
        }
      }
    }

    return result;
  };

const getBusinessInitials = (name: string, maxLetters: number = 2): string => {
  if (!name || !name.trim()) {
    return "";
  }

  const cleaned = stripSuffixes(name.trim()),

    words = cleaned
      .split(/[\s\-/]+/u)
      .map((w) => w.replace(/[^a-zA-Z0-9]/g, ""))
      .filter((w) => w.length > 0),

    // Filter out filler words for initial selection
    significant = words.filter((w) => !FILLER.has(w.toLowerCase())),

    // Fall back to all words if everything was filtered
    source = significant.length > 0 ? significant : words;

  if (source.length === 0) {
    return "";
  }

  // One letter per significant word, up to maxLetters. The default stays at 2 so existing
  // Sites keep the monogram they already show — three is opt-in.
  return source
    .slice(0, Math.max(1, maxLetters))
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

export { getBusinessInitials };
